/**
 * AppGripMenus — Phase 2.2 (Plan §8) App.tsx slice #4.
 *
 * Extracts the two inline grip context menus (B19 Block Lookup grip
 * menu + B18 MLEADER grip context menu) from `App.tsx`. These were
 * the two largest remaining inline IIFEs in the App.tsx render
 * function — 58 + 114 = 172 LOC of inline dispatch handlers.
 *
 * Both menus are pure UI overlays driven by App-scoped useState:
 *   - `blockLookupMenu` — set by Scene3DStage's lookup chevron grip
 *   - `mleaderGripMenu` — set by Scene3DStage's MLEADER grip right-click
 *
 * The component receives the menu state + setters + dispatch +
 * appendCommandFeed + t + geometryDocument as props. Lazy chunk
 * component refs (`BlockLookupMenu`, `MleaderGripContextMenu`) are
 * passed in so this component stays decoupled from import paths.
 */

import { type ComponentType } from "react";

import type {
  DrawingDocument,
  EditorCommand,
} from "@dxf-ai-editor/drawing-model";
import type { MleaderGripKind } from "@dxf-ai-editor/contracts";

export interface BlockLookupMenuState {
  entityId: string;
  parameterId: string;
  screen: { x: number; y: number };
}

export interface MleaderGripMenuState {
  entityId: string;
  gripKind: MleaderGripKind;
  gripPayload: unknown;
  gripWorld: { x: number; y: number };
  screen: { x: number; y: number };
}

export type MleaderMenuActionId =
  | "add-leader"
  | "remove-leader"
  | "add-vertex"
  | "remove-vertex"
  | "stretch";

interface BlockLookupMenuProps {
  screen: { x: number; y: number };
  parameterName: string;
  table: unknown;
  currentRowId?: string;
  onPick: (rowId: string, label: string) => void;
  onCancel: () => void;
}

interface MleaderGripContextMenuProps {
  screen: { x: number; y: number };
  gripKind: MleaderGripKind;
  onPick: (action: MleaderMenuActionId) => void;
  onCancel: () => void;
}

interface AppGripMenusProps {
  blockLookupMenu: BlockLookupMenuState | null;
  setBlockLookupMenu: (next: BlockLookupMenuState | null) => void;
  mleaderGripMenu: MleaderGripMenuState | null;
  setMleaderGripMenu: (next: MleaderGripMenuState | null) => void;
  dispatch: (command: EditorCommand) => void;
  geometryDocument: DrawingDocument;
  appendCommandFeed: (text: string) => void;
  t: (key: string, params?: Record<string, string>) => string;
  BlockLookupMenu: ComponentType<BlockLookupMenuProps>;
  MleaderGripContextMenu: ComponentType<MleaderGripContextMenuProps>;
}

export function AppGripMenus({
  blockLookupMenu,
  setBlockLookupMenu,
  mleaderGripMenu,
  setMleaderGripMenu,
  dispatch,
  geometryDocument,
  appendCommandFeed,
  t,
  BlockLookupMenu,
  MleaderGripContextMenu,
}: AppGripMenusProps): JSX.Element {
  return (
    <>
      {/* B19 — Block lookup grip menu. Mounted when the user clicks a
          dynamic-block lookup chevron grip. Picking a row writes the
          row label into the INSERT's `Param: <name>` property bag. */}
      {blockLookupMenu
        ? (() => {
            // Resolve the block + parameter referenced by this grip.
            const entity = geometryDocument.spaces
              .flatMap((s) => s.entities)
              .find((e) => e.id === blockLookupMenu.entityId);
            const blockName =
              (entity as { name?: string } | undefined)?.name ??
              (entity?.type === "Block"
                ? ((entity as unknown as { name?: string }).name ?? null)
                : null);
            const blockDef = blockName
              ? geometryDocument.blocks?.[blockName]
              : undefined;
            const dyn = blockDef?.dynamicMetadata;
            const parameter = dyn?.parameters.find(
              (p) => p.id === blockLookupMenu.parameterId,
            );
            if (!parameter || parameter.kind !== "lookup") {
              return null;
            }
            const table = (dyn?.lookupTables ?? []).find(
              (lt) => lt.id === parameter.lookupTableId,
            );
            if (!table) return null;
            return (
              <BlockLookupMenu
                screen={blockLookupMenu.screen}
                parameterName={parameter.name}
                table={table}
                {...(parameter.currentLookupRowId
                  ? { currentRowId: parameter.currentLookupRowId }
                  : {})}
                onPick={(_rowId, label) => {
                  // Apply the picked label as the parameter value via
                  // the standard properties pipeline. The
                  // `Param: <name>` slot is what
                  // buildBlockInsertPreview reads back when projecting
                  // overrides onto the entity preview.
                  dispatch({
                    type: "properties/apply",
                    source: "scene",
                    entityIds: [blockLookupMenu.entityId],
                    values: { [`Param: ${parameter.name}`]: label },
                  } as EditorCommand);
                  appendCommandFeed(
                    t("block.lookup.feed.applied", {
                      value: label,
                      name: parameter.name,
                    }),
                  );
                  setBlockLookupMenu(null);
                }}
                onCancel={() => setBlockLookupMenu(null)}
              />
            );
          })()
        : null}

      {/* B18 — MLEADER grip context menu. Mounted on right-click on
          an MLEADER grip; menu items dispatch dedicated reducer
          commands (add/remove leader, add/remove vertex). */}
      {mleaderGripMenu ? (
        <MleaderGripContextMenu
          screen={mleaderGripMenu.screen}
          gripKind={mleaderGripMenu.gripKind}
          onPick={(action: MleaderMenuActionId) => {
            const payload = mleaderGripMenu.gripPayload as
              | {
                  rootIndex?: number;
                  nodeIndex?: number;
                  lineIndex?: number;
                  vertexIndex?: number;
                }
              | undefined;
            if (action === "add-leader") {
              // Surface a prompt; the user must click in the canvas
              // to specify the new arrow point. Until prompt-mode
              // wires fully through, fallback: append a leader at
              // the picked grip's world location (visible parity
              // with AutoCAD's "click to place arrow" flow).
              dispatch({
                type: "geometry/add-leader-to-multileader",
                source: "scene",
                entityId: mleaderGripMenu.entityId,
                arrowPoint: {
                  x: mleaderGripMenu.gripWorld.x,
                  y: mleaderGripMenu.gripWorld.y,
                },
              } as EditorCommand);
              appendCommandFeed(t("mleader.feed.add_leader_prompt"));
            } else if (action === "remove-leader") {
              // Compute a flat leader index from (root, node, line).
              const rootIdx = payload?.rootIndex ?? 0;
              const nodeIdx = payload?.nodeIndex ?? 0;
              const lineIdx = payload?.lineIndex ?? 0;
              // Walk the entity to compute the flat index; fall back
              // to 0 if entity not found (reducer gracefully no-ops).
              const ent = geometryDocument.spaces
                .flatMap((s) => s.entities)
                .find((e) => e.id === mleaderGripMenu.entityId);
              let flat = 0;
              if (ent && ent.type === "Multileader") {
                outer: for (let ri = 0; ri <= rootIdx; ri++) {
                  const root = ent.leaderRoots[ri];
                  if (!root) break;
                  for (
                    let ni = 0;
                    ni < (ri === rootIdx ? nodeIdx + 1 : root.nodes.length);
                    ni++
                  ) {
                    const node = root.nodes[ni];
                    if (!node) break;
                    for (
                      let li = 0;
                      li <
                      (ri === rootIdx && ni === nodeIdx
                        ? lineIdx + 1
                        : node.lines.length);
                      li++
                    ) {
                      if (ri === rootIdx && ni === nodeIdx && li === lineIdx) {
                        break outer;
                      }
                      flat += 1;
                    }
                  }
                }
              }
              dispatch({
                type: "geometry/remove-leader-from-multileader",
                source: "scene",
                entityId: mleaderGripMenu.entityId,
                leaderIndex: flat,
                confirmed: true,
              } as EditorCommand);
              appendCommandFeed(t("mleader.feed.leader_removed"));
            } else if (action === "add-vertex") {
              // Add an intermediate vertex at the grip's world
              // location, immediately after the grip's vertex index.
              dispatch({
                type: "geometry/add-multileader-vertex",
                source: "scene",
                entityId: mleaderGripMenu.entityId,
                rootIndex: payload?.rootIndex ?? 0,
                nodeIndex: payload?.nodeIndex ?? 0,
                lineIndex: payload?.lineIndex ?? 0,
                vertexIndex: payload?.vertexIndex ?? 0,
                newPosition: {
                  x: mleaderGripMenu.gripWorld.x,
                  y: mleaderGripMenu.gripWorld.y,
                },
              } as EditorCommand);
              appendCommandFeed(t("mleader.feed.vertex_added"));
            } else if (action === "remove-vertex") {
              dispatch({
                type: "geometry/remove-multileader-vertex",
                source: "scene",
                entityId: mleaderGripMenu.entityId,
                rootIndex: payload?.rootIndex ?? 0,
                nodeIndex: payload?.nodeIndex ?? 0,
                lineIndex: payload?.lineIndex ?? 0,
                vertexIndex: payload?.vertexIndex ?? 0,
              } as EditorCommand);
              appendCommandFeed(t("mleader.feed.vertex_removed"));
            } else if (action === "stretch") {
              // Close the menu — the grip is then drag-able via the
              // existing pointerdown handler (user issues a fresh
              // left-click after the menu closes).
              appendCommandFeed(t("mleader.feed.stretch_hint"));
            }
            setMleaderGripMenu(null);
          }}
          onCancel={() => setMleaderGripMenu(null)}
        />
      ) : null}
    </>
  );
}
